import { DeckItem, CategoryItem } from '@/common/interfaces/modules/DatesAndRates';
import { Module, ActionTree, MutationTree, GetterTree, Store } from 'vuex';

import { RootState } from '@/store/types';
import { registerSubModule } from '@/utils/vueUtils';
import {
  DecksAndCabinsBlockViewModel,
  DeckViewItem,
  DeckCategoryView,
} from './DecksAndCabinsModule.types';
import { GetSvgByName, GetSvgByUrl } from './DecksAndCabinsModule.api';

export interface DecksAndCabinsModuleState extends DecksAndCabinsBlockViewModel {
  CurrentDeck: DeckViewItem | null;
  CurrentDeckCategory: DeckCategoryView | null;
  CurrentCabinCode: string;
}

export const SET_CURRENT_DECK = 'SET_CURRENT_DECK';

export const GET_SVG_REQUEST = 'GET_SVG_REQUEST';
export const GET_SVG_SUCCESS = 'GET_SVG_SUCCESS';
export const GET_SVG_ERROR = 'GET_SVG_ERROR';

export const GET_CURRENT_DECK = 'GET_CURRENT_DECK';
export const GET_DATES_AND_RATES = 'GET_DATES_AND_RATES';

export const SET_CURRENT_DECK_CATEGORY = 'SET_CURRENT_DECK_CATEGORY';
export const SET_CURRENT_CABIN_CODE = 'SET_CURRENT_CABIN_CODE';
export const SET_OPEN_DATES_AND_RATES = 'SET_OPEN_DATES_AND_RATES';
export const SET_ITINERARY_URL = 'SET_ITINERARY_URL';

export interface DeckViewResponse {
  Deck: DeckItem;
  SvgData: string;
}

export interface SvgApiData {
  Deck: DeckItem;
  ShipName: string;
}

const actions: ActionTree<DecksAndCabinsModuleState, RootState> = {
  async [GET_SVG_REQUEST]({ state, commit }, data: SvgApiData) {
    try {
      let svgData = null;
      // console.log(`${data.Deck.Name} - ${data.Deck.Image.Url} - svg ? ${data.Deck.Image.Url.includes('.svg')}`);
      if (data.Deck.Image.Url.includes('.svg')) {
        svgData = await GetSvgByUrl(data.Deck.Image.Url);
      } else {
        // Fallback if image provided is not in SVG format.
        svgData = await GetSvgByName(data.Deck.Name, data.ShipName);
      }
      if (svgData) {
        const SvgData = svgData.replace(/<svg /g, `<svg aria-label="${data.Deck.Name}"`)
        const obj: DeckViewResponse = {
          Deck: data.Deck,
          SvgData,
        };
        commit(GET_SVG_SUCCESS, obj);
      }
    } catch (error) {
      commit(GET_SVG_ERROR, error);
    }
  },
};

const mutations: MutationTree<DecksAndCabinsModuleState> = {
  [GET_SVG_ERROR](state, message: string) {
    //console.log(message);
  },
  [GET_SVG_SUCCESS](state, payload: DeckViewResponse) {
    const deck = state.Decks.find((d: any) => d.Name === payload.Deck.Name);
    if (deck) {
      deck.SvgData = payload.SvgData;
    }
  },
  [SET_CURRENT_DECK](state, payload: DeckItem | null) {
    if (payload === null) {
      state.CurrentDeck = null;
    } else {
      state.CurrentDeck = {
        Deck: payload,
        ShipName: state.ShipName,
        MonthsOfDepartures: state.MonthsOfDepartures,
      };
    }
  },
  [SET_OPEN_DATES_AND_RATES](state, payload: boolean) {
    if (payload === null) {
      state.DatesAndRatesOpen = null;
    } else {
      state.DatesAndRatesOpen = payload;
    }
  },
  [SET_ITINERARY_URL](state, payload: string) {
    if (payload === null) {
      state.itineraryUrl = null;
    } else {
      state.itineraryUrl = payload;
    }
  },
  [SET_CURRENT_DECK_CATEGORY](state, payload: CategoryItem | null) {
    if (payload === null) {
      state.CurrentDeckCategory = null;
      state.CurrentCabinCode = '';
    } else {
      state.CurrentDeckCategory = {
        Category: payload,
        CurrentDate: state.CurrentDate,
        MonthsOfDepartures: state.MonthsOfDepartures,
        ShipItinerariesLink: state.ShipItinerariesLink,
        ResetSwiper: true,
      };

      state.CurrentCabinCode = payload.Code;
    }
  },
  [SET_CURRENT_CABIN_CODE](state, payload: string) {
    if (state.CurrentCabinCode !== payload) {
      state.CurrentCabinCode = payload;
    }
  },
};

const getters: GetterTree<DecksAndCabinsModuleState, RootState> = {
  [GET_CURRENT_DECK]: (state) => state.CurrentDeck,
  [GET_DATES_AND_RATES]: (state) => state.DatesAndRatesOpen,
};

const registerModule = registerSubModule<DecksAndCabinsModuleState, DecksAndCabinsBlockViewModel>(
  (data: DecksAndCabinsBlockViewModel) => {
    return {
      namespaced: true,
      state: {
        Decks: data.Decks.map((d) => ({ ...d, SvgData: '' })),
        CurrentDeck: {},
        DatesAndRatesOpen: false,
        CurrentDeckCategory: {},
        ShipName: data.ShipName,
        ShipGuests: data.ShipGuests,
        CurrentDate: data.CurrentDate,
        MonthsOfDepartures: data.MonthsOfDepartures,
        Categories: data.Categories,
        ShipItinerariesLink: data.ShipItinerariesLink,
        CurrentCabinCode: '',
        itineraryUrl: '',
      },

      actions,
      mutations,
      getters,
    } as Module<DecksAndCabinsModuleState, RootState>;
  },
  'DecksAndCabinsModule',
);

export default async (store: Store<RootState>, data: DecksAndCabinsBlockViewModel) =>
  registerModule(store, data);
